import React from 'react';
import { Link } from 'react-router-dom';

interface CTAButtonProps {
  text: string;
  to: string;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
  isExternal?: boolean;
  disabled?: boolean;
}

const CtaButton: React.FC<CTAButtonProps> = ({
  text,
  to,
  variant = 'primary',
  size = 'md',
  className = '',
  onClick,
  isExternal = false,
  disabled = false
}) => {
  // Définir les classes de base en fonction de la variante
  let baseClasses = 'font-medium rounded-full transition-colors duration-300 text-center';
  
  // Ajouter les classes spécifiques à la variante
  switch (variant) {
    case 'primary':
      baseClasses += ' bg-primary hover:bg-primary-dark text-white shadow-sm';
      break;
    case 'secondary':
      baseClasses += ' bg-white border border-primary text-primary hover:bg-primary hover:text-white';
      break;
    case 'outline':
      baseClasses += ' bg-transparent border border-gray-300 text-gray-700 hover:bg-gray-100';
      break;
  }
  
  // Ajouter les classes spécifiques à la taille
  switch (size) {
    case 'xs':
      baseClasses += ' py-1.5 px-3 text-xs';
      break;
    case 'sm':
      baseClasses += ' py-2 px-4 text-sm';
      break;
    case 'md':
      baseClasses += ' py-3 px-6 text-base';
      break;
    case 'lg':
      baseClasses += ' py-3.5 px-7 sm:py-4 sm:px-8 text-sm sm:text-base md:text-lg';
      break;
  }
  
  // Combiner avec les classes personnalisées et ajouter la classe disabled si nécessaire
  const combinedClasses = `${baseClasses} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;
  
  // Si le lien commence par '#', c'est un lien d'ancrage
  const isAnchor = to.startsWith('#');
  
  if (isAnchor) {
    return (
      <a 
        href={to} 
        className={combinedClasses}
        onClick={disabled ? undefined : onClick}
        aria-disabled={disabled}
      >
        {text}
      </a>
    );
  }
  
  if (isExternal) {
    return (
      <a 
        href={disabled ? '#' : to} 
        className={combinedClasses}
        onClick={disabled ? undefined : onClick}
        target="_blank"
        rel="noopener noreferrer"
        aria-disabled={disabled}
      >
        {text}
      </a>
    );
  }
  
  return (
    <Link 
      to={disabled ? '#' : to} 
      className={combinedClasses}
      onClick={disabled ? undefined : onClick}
      aria-disabled={disabled}
    >
      {text}
    </Link>
  );
};

export default CtaButton;
